import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const API_BASE = "https://api.talentsexplore.com/api/";
const APP_VERSION = "0.1.0";

const getDeviceId = () => {
  const storageKey = "te-web-device-id";
  const existingId = localStorage.getItem(storageKey);
  if (existingId) {
    return existingId;
  }

  const generatedId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  localStorage.setItem(storageKey, generatedId);
  return generatedId;
};

const Wrapper = styled.section`
  min-height: 93.5vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px 48px;
  background: radial-gradient(
    circle at top,
    #ffffff 0%,
    #f6f7fb 55%,
    #eef2f8 100%
  );
`;

const Shell = styled.div`
  width: min(1120px, 100%);
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid rgba(10, 25, 56, 0.1);
  border-radius: 28px;
  box-shadow: 0 20px 48px rgba(10, 25, 56, 0.12);
  overflow: hidden;
`;

const Hero = styled.div`
  padding: 30px;
  color: #fff;
  background: linear-gradient(135deg, #0a1938 0%, #18326d 100%);
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(234, 50, 73, 0.14);
  color: #fff;
  font-weight: 700;
  font-size: 13px;
`;

const Title = styled.h1`
  margin: 14px 0 10px;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.08;
`;

const Copy = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.88);
`;

const Body = styled.div`
  padding: 30px;
`;

const Steps = styled.ul`
  margin: 18px 0 0;
  padding-left: 18px;
  display: grid;
  gap: 12px;
  color: #0f172a;
  line-height: 1.55;
`;

const Muted = styled.p`
  margin: 14px 0 0;
  color: #667085;
  line-height: 1.65;
  font-size: 14px;
`;

const Form = styled.form`
  padding: 30px;
  display: grid;
  gap: 16px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 28px;
  color: #0a1938;
`;

const SubCopy = styled.p`
  margin: 0;
  color: #667085;
  line-height: 1.7;
  font-size: 14px;
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0a1938;
`;

const Input = styled.input`
  width: 100%;
  height: 52px;
  border-radius: 14px;
  border: 1px solid rgba(10, 25, 56, 0.12);
  padding: 0 16px;
  font-size: 15px;
  outline: none;

  &:focus {
    border-color: rgba(234, 50, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(234, 50, 73, 0.08);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  min-height: 52px;
  padding: 0 18px;
  border: 0;
  border-radius: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrimaryButton = styled(Button)`
  background: #ea3249;
  color: #fff;
`;

const SecondaryButton = styled(Button)`
  background: #f2f5fb;
  color: #0a1938;
  border: 1px solid rgba(10, 25, 56, 0.08);
`;

const Status = styled.div`
  display: ${({ $show }) => ($show ? "block" : "none")};
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.6;
  ${({ $type }) =>
    $type === "success"
      ? `
        background: rgba(16, 185, 129, 0.12);
        color: #047857;
      `
      : $type === "error"
      ? `
        background: rgba(234, 50, 73, 0.1);
        color: #b42318;
      `
      : `
        background: rgba(10, 25, 56, 0.06);
        color: #0a1938;
      `}
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 25, 56, 0.58);
  display: ${({ $show }) => ($show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 50;
`;

const Modal = styled.div`
  width: min(520px, 100%);
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  padding: 26px;
`;

const ModalTitle = styled.h3`
  margin: 12px 0 10px;
  font-size: 24px;
  color: #0a1938;
`;

const ModalText = styled.p`
  margin: 0;
  color: #667085;
  line-height: 1.7;
`;

const Danger = styled.div`
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(234, 50, 73, 0.1);
  color: #b42318;
  line-height: 1.6;
  font-size: 14px;
`;

const DeleteAccountFlow = ({ variant = "delete-page" }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [showConfirm, setShowConfirm] = useState(false);
  const [token, setToken] = useState("");
  const [deleteDetails, setDeleteDetails] = useState(null);

  const heading = useMemo(
    () =>
      variant === "home"
        ? "Delete your account"
        : "Login to delete your account",
    [variant]
  );

  const helperText = useMemo(
    () =>
      variant === "home"
        ? "This page gives users a direct path to delete their account and associated data."
        : "Sign in first so we can verify that you own the account before asking for final confirmation.",
    [variant]
  );

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      const response = await fetch(`${API_BASE}auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          deviceId: getDeviceId(),
          appVersion: APP_VERSION,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.status !== "7400") {
        throw new Error(data?.message || "Login failed");
      }
      const accessToken =
        data?.response?.accessToken || data?.response?.token || "";
      const refreshToken = data?.response?.refreshToken || "";
      if (accessToken) {
        localStorage.setItem("token", accessToken);
      }
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      setToken(accessToken);
      setDeleteDetails(data?.response || null);
      setShowConfirm(true);
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Login failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setStatus({ type: "", message: "" });
    try {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}user/deleteAccount`, {
        method: "DELETE",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.status !== "7400") {
        throw new Error(data?.message || "Unable to delete account");
      }
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      setShowConfirm(false);
      setStatus({
        type: "success",
        message: "Your account has been deleted successfully.",
      });
      setTimeout(() => navigate("/"), 1400);
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Unable to delete account",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Wrapper>
      <Shell>
        <Card>
          <Hero>
            <Pill>TalentExplore account deletion</Pill>
            <Title>{heading}</Title>
            <Copy>{helperText}</Copy>
          </Hero>
          <Body>
            <h2 style={{ margin: 0, color: "#0a1938", fontSize: "22px" }}>
              What happens next
            </h2>
            <Steps>
              <li>Enter the account email and password.</li>
              <li>We verify the account with the app API.</li>
              <li>You confirm the deletion one final time.</li>
              <li>The account is deleted and you are returned home.</li>
            </Steps>
            <Muted>
              Data deleted: profile, posts, shared links, comments, chats, and
              related user-generated content, subject to legal or security
              retention requirements.
            </Muted>
          </Body>
        </Card>

        <Card>
          <Form onSubmit={login}>
            <div>
              <Heading>Login</Heading>
              <SubCopy>
                Use the account credentials for the user account you want to
                delete.
              </SubCopy>
            </div>

            <Field>
              Email
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </Field>

            <Field>
              Password
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </Field>

            <PrimaryButton type="submit" disabled={loading}>
              {loading ? "Logging in..." : "Login to Continue"}
            </PrimaryButton>

            <ButtonRow>
              <SecondaryButton type="button" onClick={() => navigate("/")}>
                Back to home
              </SecondaryButton>
            </ButtonRow>

            <Status $show={Boolean(status.message)} $type={status.type}>
              {status.message}
            </Status>
          </Form>
        </Card>
      </Shell>

      <ModalBackdrop $show={showConfirm}>
        <Modal>
          <Pill>Final confirmation</Pill>
          <ModalTitle>Delete this account permanently?</ModalTitle>
          <ModalText>
            This action cannot be undone. We have verified the account and are
            ready to delete it.
          </ModalText>
          <Danger>
            {deleteDetails?.email
              ? `Signed in as ${deleteDetails.email}. If you continue, the account will be deleted through the API.`
              : "If you continue, the account will be deleted through the API."}
          </Danger>
          <ButtonRow style={{ marginTop: "22px" }}>
            <SecondaryButton
              type="button"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Yes, delete my account"}
            </PrimaryButton>
          </ButtonRow>
          <Status $show={Boolean(status.message)} $type={status.type}>
            {status.message}
          </Status>
        </Modal>
      </ModalBackdrop>
    </Wrapper>
  );
};

export default DeleteAccountFlow;
